/** Entry point of the phase-D sky package: registers objects, talk, drawers and day hooks. Imported once from main.ts. */
import { bus } from '../core/bus';
import { has } from '../core/effects';
import { S, hasGame } from '../core/game';
import { dayId } from '../core/state';
import { sfx, setStepMode } from '../audio/sfx';
import { distantSteps, updateAmbience } from '../audio/ambience';
import { registerDoorRule, registerItemUse, registerObjects } from './registry';
import { registerPropDrawers } from '../render/propRegistry';
import { groundWet, lightning, weatherDebug, onSkyFrame, onThunder, setSkySource, startShake } from '../render/weather';
import { drawCalendar, drawKit, drawUmbrellaStand } from '../render/skyProps';
import { injectSkyStyles } from '../ui/skyStyle';
import { openCalendar, openKit, openUmbrellaStand, useUmbrella } from '../ui/skyPanels';
import { toast } from '../core/effects';
import { m } from '../i18n';
import type { World } from '../world/world';
import { dayNum } from './calendar';
import { bindDisastersWorld, disastersDayStart, disastersTick, earthquake, typhoonDoorRule } from './disasters';
import { forceWeather, gloomNow, rainPower, weatherFor, windPower, WEATHER_KINDS, type WeatherKind } from './weather';

injectSkyStyles();

registerObjects({
  sky_calendar: openCalendar,
  sky_kit: openKit,
  sky_umbrellas: openUmbrellaStand,
});
registerItemUse('umbrella', useUmbrella);
registerDoorRule(typhoonDoorRule);
registerPropDrawers({
  objects: { sky_calendar: drawCalendar, sky_kit: drawKit, sky_umbrellas: drawUmbrellaStand },
  // The calendar shows today's date, so it's redrawn instead of cached.
  animatedObjects: ['sky_calendar'],
});

setSkySource(() => {
  if (!hasGame()) return { rain: 0, wind: 0, gloom: 0, thunder: false, umbrella: false };
  const rain = rainPower();
  return { rain, wind: windPower(), gloom: gloomNow(), thunder: weatherFor(dayNum()).thunder && rain > 0.3, umbrella: has('umbrella') };
});
onThunder((delay) => window.setTimeout(() => sfx('thunder'), delay * 1000));

let world: World | null = null;
setStepMode(() => {
  if (!world) return 'normal';
  if (world.map.id === 'school' && world.areaKey() === 'corridor') return 'hall';
  return !world.map.indoor && groundWet() > 0.3 ? 'wet' : 'normal';
});

const BELLS = [8 * 60, 12 * 60, 16 * 60];
const SCHOOL_YARD = new Set(['schoolGate', 'yard', 'field']);
let prevTime = -1;

onSkyFrame((w, dt) => {
  if (!world) {
    world = w;
    bindDisastersWorld(w);
  }
  attachDebug();
  if (!hasGame()) return;
  const s = S();
  updateAmbience(dt, rainPower(), windPower(), w.map.indoor);
  // The school bell (Westminster chimes) at 8:00, 12:00 and 16:00, heard on the school map.
  if (prevTime >= 0 && s.time > prevTime && s.time - prevTime < 5) {
    for (const b of BELLS) if (prevTime < b && s.time >= b && (w.map.id === 'school' || (w.map.id === 'town' && SCHOOL_YARD.has(w.areaKey())))) sfx('bell');
  }
  prevTime = s.time;
  // Other kids running down the corridor on school days.
  if (w.map.id === 'school' && dayId(s) === 'fri' && s.time > 7 * 60 + 30 && s.time < 17 * 60 && w.areaKey() === 'corridor' && Math.random() < dt / 7) distantSteps();
  disastersTick(w, dt);
});

bus.on('dayStart', () => {
  prevTime = -1;
  disastersDayStart();
  // School days start at 8:00 sharp: the first bell greets you in the classroom.
  window.setTimeout(() => {
    if (world?.map.id === 'school' && hasGame() && S().time < 8 * 60 + 20) sfx('bell');
  }, 1800);
});

// --- Debug helpers: window.__debug.sky ------------------------------------------------
let debugDone = false;
function attachDebug(): void {
  if (debugDone) return;
  const d = (window as unknown as { __debug?: Record<string, unknown> }).__debug;
  if (!d) return;
  debugDone = true;
  d.sky = {
    /** Force today's weather; rain/storm/typhoon start right away. */
    setWeather(kind: WeatherKind | null) {
      if (kind && !WEATHER_KINDS.includes(kind)) return `kinds: ${WEATHER_KINDS.join(', ')}`;
      forceWeather(dayNum(), kind, true);
      bus.emit('change');
      return weatherFor(dayNum());
    },
    quake: () => earthquake(true),
    typhoonTomorrow() {
      forceWeather(dayNum() + 1, 'typhoon');
      toast(m().sky.toasts.typhoonEve);
      bus.emit('change');
      return weatherFor(dayNum() + 1);
    },
    lightning,
    shake: (s = 3) => startShake(s, 3),
    wet: () => groundWet(),
    state: (reset = false) => weatherDebug(reset),
  };
}
